import { Link } from "@remix-run/react";
import { trans } from "@mongez/localization";
import { ChevronLeft, Video } from "lucide-react";
import { ShortCard } from "../ShortCard/ShortCard";
import type { Short } from "../../types";

/**
 * Horizontal rail of shorts — used at the top of the home page and above
 * the lessons grid on category pages.
 *
 * Self-contained: the parent passes a pre-fetched list (so SSR + caching
 * are fine), and this component only handles layout.
 */
type ShortsRailProps = {
  shorts: Short[];
  title?: string;
  /** Optional "view more" target. Defaults to `/shorts`. */
  viewMoreHref?: string;
};

export function ShortsRail({ shorts, title, viewMoreHref }: ShortsRailProps) {
  if (!shorts || shorts.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-4 flex items-end justify-between">
        <div className="flex items-center gap-2">
          <Video size={22} className="text-rose-500" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
            {title ?? trans("shorts.railTitle")}
          </h2>
        </div>
        <Link
          to={viewMoreHref ?? "/shorts"}
          className="inline-flex items-center gap-1 text-sm font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400">
          {trans("shorts.viewMore")}
          <ChevronLeft size={16} />
        </Link>
      </div>

      <div
        className="flex gap-3 overflow-x-auto pb-3 sm:gap-4"
        style={{ scrollSnapType: "x mandatory" }}>
        {shorts.map(short => (
          <div key={short.id} style={{ scrollSnapAlign: "start" }}>
            <ShortCard short={short} />
          </div>
        ))}
      </div>
    </section>
  );
}
